import { useCallback, useEffect, useState } from 'react';
import { Toaster } from 'sonner';
import { storage } from '@/services/storage';
import { AdminLogin } from './AdminLogin';
import { AdminLayout } from './AdminLayout';
import { Dashboard } from './pages/Dashboard';
import { AIConfiguration } from './pages/AIConfiguration';
import { Users } from './pages/Users';
import { Enrollment } from './pages/Enrollment';
import { Payments } from './pages/Payments';

// AdminRoute is the admin portal's internal page set. Paths under
// /admin/* map here without involving the taskpane router.
export type AdminRoute = 'dashboard' | 'ai-config' | 'users' | 'enrollment' | 'payments';

const ROUTE_KEY = 'admin_last_route';

function parsePathRoute(): AdminRoute {
  const p = window.location.pathname.replace(/\/+$/, '');
  if (p.endsWith('/ai-config')) return 'ai-config';
  if (p.endsWith('/users')) return 'users';
  if (p.endsWith('/enrollment')) return 'enrollment';
  if (p.endsWith('/payments')) return 'payments';
  // Fallback: sessionStorage (survives token-expiry re-login + nginx URL rewrites)
  const saved = sessionStorage.getItem(ROUTE_KEY) as AdminRoute | null;
  if (saved === 'users' || saved === 'ai-config' || saved === 'enrollment' || saved === 'payments') return saved;
  return 'dashboard';
}

export function AdminApp() {
  // Admin portal forces light mode. Dark-mode variants across the admin
  // surfaces (tables, charts, modals) are incomplete after the deploy;
  // rather than ship a half-dark/half-light experience that hides email
  // cells, we pin the admin portal to light until the full dark sweep
  // lands. The taskpane still honors user prefs via useTheme().
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  const [authed, setAuthed] = useState<boolean>(() => {
    const u = storage.getUser<{ role?: string }>();
    return !!storage.getAccess() && u?.role === 'admin';
  });
  // Route is the source of truth in React state. The URL is kept in
  // sync via pushState/popstate, but we don't rely on the history
  // round-trip to drive re-renders — that was flaky in dev.
  const [route, setRoute] = useState<AdminRoute>(parsePathRoute);

  const navigate = useCallback((to: AdminRoute) => {
    setRoute(to);
    sessionStorage.setItem(ROUTE_KEY, to);
    const path = to === 'dashboard' ? '/admin' : `/admin/${to}`;
    if (window.location.pathname !== path) {
      window.history.pushState({ route: to }, '', path);
    }
  }, []);

  // Back/forward button support — mirror URL changes back into state.
  useEffect(() => {
    const onPop = () => setRoute(parsePathRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    // If the user gets logged out mid-session (e.g. token expired and
    // refresh failed), fall back to the login screen.
    const onExpired = () => setAuthed(false);
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const logout = () => {
    storage.clear();
    setAuthed(false);
    window.history.pushState({}, '', '/admin/login');
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-surface text-text-primary">
        <AdminLogin
          onSuccess={() => {
            setAuthed(true);
            navigate(parsePathRoute()); // restore last page, not always dashboard
          }}
        />
        <Toaster position="top-center" richColors closeButton />
      </div>
    );
  }

  return (
    <AdminLayout active={route} onNavigate={navigate} onLogout={logout}>
      {route === 'dashboard' && <Dashboard />}
      {route === 'ai-config' && <AIConfiguration />}
      {route === 'users' && <Users />}
      {route === 'enrollment' && <Enrollment />}
      {route === 'payments' && <Payments />}
      <Toaster position="top-center" richColors closeButton />
    </AdminLayout>
  );
}
